/**
 * Pre-built funnel templates for novice marketers
 * Each template includes nodes and edges that can be applied to the canvas
 */

export const FUNNEL_TEMPLATES = [
    {
        id: 'awareness-only',
        name: 'Awareness Campaign',
        description: 'Single TOFU campaign for brand awareness. Perfect for introducing your brand to new audiences.',
        stages: ['TOFU'],
        difficulty: 'beginner',
        nodes: [
            {
                type: 'campaign',
                data: {
                    name: 'Awareness Campaign',
                    objective: 'AWARENESS',
                    funnelStage: 'TOFU',
                    budget: { amount: 50, type: 'daily' },
                    status: 'draft'
                },
                position: { x: 100, y: 100 }
            },
            {
                type: 'adset',
                data: {
                    name: 'Broad Audience',
                    funnelStage: 'TOFU',
                    targeting: {
                        ageMin: 18,
                        ageMax: 65,
                        genders: [],
                        locations: [],
                        interests: []
                    },
                    status: 'draft'
                },
                position: { x: 100, y: 300 }
            },
            {
                type: 'ad',
                data: {
                    name: 'Awareness Ad 1',
                    funnelStage: 'TOFU',
                    headline: '',
                    primaryText: '',
                    cta: 'LEARN_MORE',
                    status: 'draft'
                },
                position: { x: 100, y: 480 }
            }
        ],
        edges: [
            { sourceIndex: 0, targetIndex: 1, sourceHandle: 'adset-out', targetHandle: 'campaign-in' },  // Campaign → Ad Set
            { sourceIndex: 1, targetIndex: 2, sourceHandle: 'ad-out', targetHandle: 'adset-in' }  // Ad Set → Ad
        ]
    },
    {
        id: 'lead-gen',
        name: 'Lead Generation Funnel',
        description: 'TOFU awareness + MOFU lead capture with automated retargeting. Build awareness and convert interested visitors into leads.',
        stages: ['TOFU', 'MOFU'],
        difficulty: 'intermediate',
        nodes: [
            // TOFU Campaign (index 0)
            {
                type: 'campaign',
                data: {
                    name: 'Awareness Campaign',
                    objective: 'AWARENESS',
                    funnelStage: 'TOFU',
                    budget: { amount: 40, type: 'daily' },
                    status: 'draft'
                },
                position: { x: 100, y: 100 }
            },
            // TOFU Ad Set (index 1)
            {
                type: 'adset',
                data: {
                    name: 'Cold Audience',
                    funnelStage: 'TOFU',
                    targeting: { ageMin: 25, ageMax: 55, genders: [], locations: [], interests: [] },
                    status: 'draft'
                },
                position: { x: 100, y: 300 }
            },
            // TOFU Ad (index 2)
            {
                type: 'ad',
                data: {
                    name: 'Brand Intro Ad',
                    funnelStage: 'TOFU',
                    headline: '',
                    primaryText: '',
                    cta: 'LEARN_MORE',
                    status: 'draft'
                },
                position: { x: 100, y: 480 }
            },
            // Retargeting Pool TOFU→MOFU (index 3)
            {
                type: 'retargetingPool',
                data: {
                    name: 'TOFU Engagers',
                    poolType: 'engagement',
                    sourceStage: 'TOFU',
                    targetStage: 'MOFU',
                    trigger: { type: 'engagement', threshold: 1, lookbackDays: 30 },
                    estimatedSize: 0,
                    isActive: true,
                    status: 'draft'
                },
                position: { x: 300, y: 400 }
            },
            // MOFU Campaign (index 4)
            {
                type: 'campaign',
                data: {
                    name: 'Lead Generation',
                    objective: 'LEADS',
                    funnelStage: 'MOFU',
                    budget: { amount: 60, type: 'daily' },
                    status: 'draft'
                },
                position: { x: 500, y: 100 }
            },
            // MOFU Ad Set (index 5)
            {
                type: 'adset',
                data: {
                    name: 'Engaged Visitors',
                    funnelStage: 'MOFU',
                    targeting: { ageMin: 25, ageMax: 55, genders: [], locations: [], interests: [] },
                    status: 'draft'
                },
                position: { x: 500, y: 300 }
            },
            // MOFU Ad (index 6)
            {
                type: 'ad',
                data: {
                    name: 'Lead Magnet Ad',
                    funnelStage: 'MOFU',
                    headline: '',
                    primaryText: '',
                    cta: 'SIGN_UP',
                    status: 'draft'
                },
                position: { x: 500, y: 480 }
            }
        ],
        edges: [
            { sourceIndex: 0, targetIndex: 1, sourceHandle: 'adset-out', targetHandle: 'campaign-in' },  // TOFU Campaign → TOFU Ad Set
            { sourceIndex: 1, targetIndex: 2, sourceHandle: 'ad-out', targetHandle: 'adset-in' },  // TOFU Ad Set → TOFU Ad
            { sourceIndex: 0, targetIndex: 3, sourceHandle: 'retarget-out', targetHandle: 'pool-in' },  // TOFU Campaign → Retargeting Pool (from yellow dot)
            { sourceIndex: 3, targetIndex: 5, sourceHandle: 'pool-out', targetHandle: 'campaign-in' },  // Retargeting Pool → MOFU Ad Set
            { sourceIndex: 4, targetIndex: 5, sourceHandle: 'adset-out', targetHandle: 'campaign-in' },  // MOFU Campaign → MOFU Ad Set
            { sourceIndex: 5, targetIndex: 6, sourceHandle: 'ad-out', targetHandle: 'adset-in' }   // MOFU Ad Set → MOFU Ad
        ]
    },
    {
        id: 'full-funnel',
        name: 'Full Funnel',
        description: 'Complete TOFU + MOFU + BOFU funnel with automated retargeting and high-intent segments. Guide users from awareness to conversion.',
        stages: ['TOFU', 'MOFU', 'BOFU'],
        difficulty: 'advanced',
        nodes: [
            // TOFU Campaign (index 0)
            {
                type: 'campaign',
                data: {
                    name: 'Awareness',
                    objective: 'AWARENESS',
                    funnelStage: 'TOFU',
                    budget: { amount: 30, type: 'daily' },
                    status: 'draft'
                },
                position: { x: 100, y: 100 }
            },
            // TOFU Ad Set (index 1)
            {
                type: 'adset',
                data: {
                    name: 'Prospecting',
                    funnelStage: 'TOFU',
                    targeting: { ageMin: 18, ageMax: 65, genders: [], locations: [], interests: [] },
                    status: 'draft'
                },
                position: { x: 100, y: 280 }
            },
            // TOFU Ad (index 2)
            {
                type: 'ad',
                data: {
                    name: 'Intro Video',
                    funnelStage: 'TOFU',
                    headline: '',
                    cta: 'LEARN_MORE',
                    status: 'draft'
                },
                position: { x: 100, y: 440 }
            },
            // Retargeting Pool TOFU→MOFU (index 3)
            {
                type: 'retargetingPool',
                data: {
                    name: 'Video Viewers',
                    poolType: 'video_viewer',
                    sourceStage: 'TOFU',
                    targetStage: 'MOFU',
                    trigger: { type: 'video_view', threshold: 50, lookbackDays: 30 },
                    estimatedSize: 0,
                    isActive: true,
                    status: 'draft'
                },
                position: { x: 300, y: 360 }
            },
            // MOFU Campaign (index 4)
            {
                type: 'campaign',
                data: {
                    name: 'Consideration',
                    objective: 'TRAFFIC',
                    funnelStage: 'MOFU',
                    budget: { amount: 40, type: 'daily' },
                    status: 'draft'
                },
                position: { x: 500, y: 100 }
            },
            // MOFU Ad Set (index 5)
            {
                type: 'adset',
                data: {
                    name: 'Website Visitors',
                    funnelStage: 'MOFU',
                    targeting: { ageMin: 18, ageMax: 65, genders: [], locations: [], interests: [] },
                    status: 'draft'
                },
                position: { x: 500, y: 280 }
            },
            // MOFU A/B Test (index 6)
            {
                type: 'abtest',
                data: {
                    name: 'Message Test',
                    funnelStage: 'MOFU',
                    trafficSplit: [50, 50],
                    winnerCriteria: 'CTR',
                    duration: 14,
                    status: 'draft'
                },
                position: { x: 500, y: 440 }
            },
            // MOFU Ad A (index 7)
            {
                type: 'ad',
                data: {
                    name: 'Testimonial Ad',
                    funnelStage: 'MOFU',
                    headline: '',
                    cta: 'SHOP_NOW',
                    variant: 'A',
                    status: 'draft'
                },
                position: { x: 420, y: 620 }
            },
            // MOFU Ad B (index 8)
            {
                type: 'ad',
                data: {
                    name: 'Features Ad',
                    funnelStage: 'MOFU',
                    headline: '',
                    cta: 'SHOP_NOW',
                    variant: 'B',
                    status: 'draft'
                },
                position: { x: 580, y: 620 }
            },
            // Retargeting Pool MOFU→BOFU (index 9)
            {
                type: 'retargetingPool',
                data: {
                    name: 'Site Visitors',
                    poolType: 'website',
                    sourceStage: 'MOFU',
                    targetStage: 'BOFU',
                    trigger: { type: 'website_visit', threshold: 1, lookbackDays: 14 },
                    estimatedSize: 0,
                    isActive: true,
                    status: 'draft'
                },
                position: { x: 700, y: 520 }
            },
            // BOFU Campaign (index 10)
            {
                type: 'campaign',
                data: {
                    name: 'Conversion',
                    objective: 'CONVERSIONS',
                    funnelStage: 'BOFU',
                    budget: { amount: 50, type: 'daily' },
                    status: 'draft'
                },
                position: { x: 900, y: 100 }
            },
            // High Intent Segment (index 11)
            {
                type: 'segment',
                data: {
                    name: 'High Intent Buyers',
                    segmentType: 'high_intent',
                    sourceStages: ['TOFU', 'MOFU'],
                    funnelStage: 'BOFU',
                    intentSignals: {
                        engagementDepth: { enabled: true, minAdViews: 3, minVideoWatchPercent: 75, clickThrough: true },
                        websiteBehavior: { enabled: true, pagesVisited: ['/pricing'], cartActivity: true, minTimeOnSite: 60 },
                        recencyFrequency: { enabled: true, lastInteractionDays: 7, minInteractions: 2 },
                        stageProgression: { enabled: false }
                    },
                    estimatedSize: 15000,
                    status: 'draft'
                },
                position: { x: 900, y: 220 }
            },
            // BOFU Ad Set (index 12)
            {
                type: 'adset',
                data: {
                    name: 'High Intent Audience',
                    funnelStage: 'BOFU',
                    targeting: { ageMin: 18, ageMax: 65, genders: [], locations: [], interests: [] },
                    status: 'draft'
                },
                position: { x: 900, y: 380 }
            },
            // BOFU Ad (index 13)
            {
                type: 'ad',
                data: {
                    name: 'Limited Offer',
                    funnelStage: 'BOFU',
                    headline: '',
                    cta: 'BUY_NOW',
                    status: 'draft'
                },
                position: { x: 900, y: 540 }
            }
        ],
        edges: [
            // TOFU tree
            { sourceIndex: 0, targetIndex: 1, sourceHandle: 'adset-out', targetHandle: 'campaign-in' },   // TOFU Campaign → TOFU Ad Set
            { sourceIndex: 1, targetIndex: 2, sourceHandle: 'ad-out', targetHandle: 'adset-in' },   // TOFU Ad Set → TOFU Ad
            { sourceIndex: 0, targetIndex: 3, sourceHandle: 'retarget-out', targetHandle: 'pool-in' },   // TOFU Campaign → Video Viewers Pool (from yellow dot)
            // MOFU tree
            { sourceIndex: 3, targetIndex: 5, sourceHandle: 'pool-out', targetHandle: 'campaign-in' },   // Video Viewers Pool → MOFU Ad Set
            { sourceIndex: 4, targetIndex: 5, sourceHandle: 'adset-out', targetHandle: 'campaign-in' },   // MOFU Campaign → MOFU Ad Set
            { sourceIndex: 5, targetIndex: 6, sourceHandle: 'ad-out', targetHandle: 'adset-in' },   // MOFU Ad Set → A/B Test
            { sourceIndex: 6, targetIndex: 7, sourceHandle: 'variant-out', targetHandle: 'adset-in' },   // A/B Test → Ad A
            { sourceIndex: 6, targetIndex: 8, sourceHandle: 'variant-out', targetHandle: 'adset-in' },   // A/B Test → Ad B
            { sourceIndex: 4, targetIndex: 9, sourceHandle: 'retarget-out', targetHandle: 'pool-in' },   // MOFU Campaign → Site Visitors Pool (from yellow dot)
            // BOFU tree
            { sourceIndex: 9, targetIndex: 12, sourceHandle: 'pool-out', targetHandle: 'campaign-in' },  // Site Visitors Pool → BOFU Ad Set
            { sourceIndex: 10, targetIndex: 11, sourceHandle: 'retarget-out', targetHandle: 'source-in' }, // BOFU Campaign → Segment (from yellow dot)
            { sourceIndex: 11, targetIndex: 12, sourceHandle: 'target-out', targetHandle: 'campaign-in' }, // Segment → BOFU Ad Set
            { sourceIndex: 10, targetIndex: 12, sourceHandle: 'adset-out', targetHandle: 'campaign-in' }, // BOFU Campaign → BOFU Ad Set
            { sourceIndex: 12, targetIndex: 13, sourceHandle: 'ad-out', targetHandle: 'adset-in' }  // BOFU Ad Set → BOFU Ad
        ]
    },
    {
        id: 'retargeting-only',
        name: 'Retargeting Only',
        description: 'MOFU + BOFU for warm audiences with intent-based targeting. Re-engage visitors who already know your brand.',
        stages: ['MOFU', 'BOFU'],
        difficulty: 'intermediate',
        nodes: [
            // MOFU Campaign (index 0)
            {
                type: 'campaign',
                data: {
                    name: 'Re-engagement',
                    objective: 'TRAFFIC',
                    funnelStage: 'MOFU',
                    budget: { amount: 35, type: 'daily' },
                    status: 'draft'
                },
                position: { x: 500, y: 100 }
            },
            // MOFU Ad Set (index 1)
            {
                type: 'adset',
                data: {
                    name: 'Cart Abandoners',
                    funnelStage: 'MOFU',
                    targeting: { ageMin: 18, ageMax: 65, genders: [], locations: [], interests: [] },
                    status: 'draft'
                },
                position: { x: 500, y: 280 }
            },
            // MOFU Ad (index 2)
            {
                type: 'ad',
                data: {
                    name: 'Come Back Ad',
                    funnelStage: 'MOFU',
                    headline: '',
                    cta: 'SHOP_NOW',
                    status: 'draft'
                },
                position: { x: 500, y: 450 }
            },
            // Warm Audience Segment (index 3)
            {
                type: 'segment',
                data: {
                    name: 'Warm Audience',
                    segmentType: 'custom',
                    sourceStages: ['MOFU'],
                    funnelStage: 'BOFU',
                    intentSignals: {
                        engagementDepth: { enabled: false },
                        websiteBehavior: { enabled: true, pagesVisited: [], cartActivity: true, minTimeOnSite: 30 },
                        recencyFrequency: { enabled: true, lastInteractionDays: 14, minInteractions: 1 },
                        stageProgression: { enabled: false }
                    },
                    estimatedSize: 25000,
                    status: 'draft'
                },
                position: { x: 700, y: 350 }
            },
            // BOFU Campaign (index 4)
            {
                type: 'campaign',
                data: {
                    name: 'Final Push',
                    objective: 'CONVERSIONS',
                    funnelStage: 'BOFU',
                    budget: { amount: 45, type: 'daily' },
                    status: 'draft'
                },
                position: { x: 900, y: 100 }
            },
            // BOFU Ad Set (index 5)
            {
                type: 'adset',
                data: {
                    name: 'Past Purchasers',
                    funnelStage: 'BOFU',
                    targeting: { ageMin: 18, ageMax: 65, genders: [], locations: [], interests: [] },
                    status: 'draft'
                },
                position: { x: 900, y: 280 }
            },
            // BOFU Ad (index 6)
            {
                type: 'ad',
                data: {
                    name: 'Exclusive Offer',
                    funnelStage: 'BOFU',
                    headline: '',
                    cta: 'BUY_NOW',
                    status: 'draft'
                },
                position: { x: 900, y: 450 }
            }
        ],
        edges: [
            // MOFU tree
            { sourceIndex: 0, targetIndex: 1, sourceHandle: 'adset-out', targetHandle: 'campaign-in' },  // MOFU Campaign → MOFU Ad Set
            { sourceIndex: 1, targetIndex: 2, sourceHandle: 'ad-out', targetHandle: 'adset-in' },  // MOFU Ad Set → MOFU Ad
            { sourceIndex: 0, targetIndex: 3, sourceHandle: 'retarget-out', targetHandle: 'source-in' },  // MOFU Campaign → Warm Audience Segment (from yellow dot)
            // BOFU tree
            { sourceIndex: 3, targetIndex: 5, sourceHandle: 'target-out', targetHandle: 'campaign-in' },  // Segment → BOFU Ad Set
            { sourceIndex: 4, targetIndex: 5, sourceHandle: 'adset-out', targetHandle: 'campaign-in' },  // BOFU Campaign → BOFU Ad Set
            { sourceIndex: 5, targetIndex: 6, sourceHandle: 'ad-out', targetHandle: 'adset-in' }   // BOFU Ad Set → BOFU Ad
        ]
    }
];

// Helper to get template by ID
export const getTemplateById = (id) => FUNNEL_TEMPLATES.find(t => t.id === id);

// Helper to filter templates by stage
export const getTemplatesByStage = (stage) => FUNNEL_TEMPLATES.filter(t => t.stages.includes(stage));

// Helper to filter templates by difficulty
export const getTemplatesByDifficulty = (difficulty) => FUNNEL_TEMPLATES.filter(t => t.difficulty === difficulty);

export default FUNNEL_TEMPLATES;
